/**
 * Icon Component
 *
 * A wrapper for Lucide icons with consistent sizing from the design system.
 * Replaces raw Tailwind like`className="h-4 w-4"`or`className="h-5 w-5"`.
 *
 * @example
 *```tsx
 * import { Icon } from '@/ui';
 * import { ChevronRight, Check, X } from 'lucide-react';
 *
 * // Using size prop
 * <Icon icon={ChevronRight} size="sm"/>
 * <Icon icon={Check} size="md"/>
 * <Icon icon={X} size="lg"color="destructive"/>
 *
 * // Using numeric size (Tailwind scale)
 * <Icon icon={ChevronRight} size={4} /> // h-4 w-4
 * <Icon icon={Check} size={5} /> // h-5 w-5
 *
 * // With additional styling
 * <Icon icon={Check} size="sm"color="success"/>
 *```
 *
 * @see docs/ui-contract.md
 */

import * as React from 'react';
import type {VariantProps} from 'class-variance-authority';
import {cn} from '@/shared/utils/utils';
import type {LucideIcon, LucideProps} from 'lucide-react';
import {AlertCircle, AlertTriangle, Check, Loader2,} from 'lucide-react';
import {iconVariants} from './Icon.variants';

// =============================================================================
// TYPES
// =============================================================================

export interface IconProps
 extends Omit<LucideProps, 'size' | 'color'>,
 VariantProps<typeof iconVariants> {
 /**
 * The Lucide icon component to render
 */
 icon: LucideIcon;
 /**
 * Additional class names (explicitly allows undefined for exactOptionalPropertyTypes)
 */
 className?: string | undefined;
 /**
 * Accessible label for screen readers
 */
 'aria-label'?: string | undefined;
}

// =============================================================================
// COMPONENT
// =============================================================================

/**
 * Icon - Consistent icon sizing wrapper
 *
 * Use Icon for all Lucide icons to ensure consistent sizing.
 * This replaces patterns like:
 * -`<ChevronRight className="h-4 w-4"/>`
 * -`<Check className="h-5 w-5 text-green-500"/>`
 * -`<Loader2 className="h-4 w-4 animate-spin"/>`
 */
export function Icon({
 icon: IconComponent,
 size,
 color,
 animate,
 className,
 'aria-label': ariaLabel,
 ...props
}: IconProps): React.ReactElement {
 return (
 <IconComponent
 className={cn(iconVariants({ size, color, animate }), className)}
 aria-label={ariaLabel}
 aria-hidden={!ariaLabel}
 {...props}
 />
 );
}

// =============================================================================
// CONVENIENCE COMPONENTS
// =============================================================================

/**
 * SpinnerIcon - Loading spinner icon
 */
export function SpinnerIcon({
 size = 'md',
 className,
 ...props
}: Omit<IconProps, 'icon' | 'animate'>): React.ReactElement {
 return (
 <Icon
 icon={Loader2}
 size={size}
 animate="spin"
 color="muted"
 className={className}
 aria-label="Loading"
 {...props}
 />
 );
}

/**
 * CheckIcon - Success check icon
 */
export function CheckIcon({
 size = 'md',
 className,
 ...props
}: Omit<IconProps, 'icon' | 'color'>): React.ReactElement {
 return (
 <Icon
 icon={Check}
 size={size}
 color="success"
 className={className}
 {...props}
 />
 );
}

/**
 * ErrorIcon - Error/destructive icon
 */
export function ErrorIcon({
 size = 'md',
 className,
 ...props
}: Omit<IconProps, 'icon' | 'color'>): React.ReactElement {
 return (
 <Icon
 icon={AlertCircle}
 size={size}
 color="destructive"
 className={className}
 {...props}
 />
 );
}

/**
 * WarningIcon - Warning icon
 */
export function WarningIcon({
 size = 'md',
 className,
 ...props
}: Omit<IconProps, 'icon' | 'color'>): React.ReactElement {
 return (
 <Icon
 icon={AlertTriangle}
 size={size}
 color="warning"
 className={className}
 {...props}
 />
 );
}
